import { FunctionComponent, useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/store";
import { useTranslation } from "react-i18next";
import { selectSmsAuth } from "@store/sessionReducer";
import { AuthError } from "../../components/AuthError/AuthError";
import { AuthInstructions } from "../../components/AuthInstructions/AuthInstructions";
import { OtpInput } from "@ui-kit/OtpInput/OtpInput";
import { AuthLinkButton } from "../../components/AuthLinkButton/AuthLinkButton";
import { formatAndObscurePhoneNumber } from "@lib/formatPhoneNumber";
import { sendSmsLoginOtpAction } from "@store/auth/actions/sendSmsLoginOtpAction";
import { verifySmsLoginOtpAction } from "@store/auth/actions/verifySmsLoginOtpAction";
import { FormStatus } from "app/javascript/src/modules/components/formFields/FormStatus";
interface SmsProps {}

const SmsVerify: FunctionComponent<SmsProps> = () => {
  const [state, setState] = useState<{ status: FormStatus }>({
    status: "idle",
  });
  const smsAuth = useAppSelector(selectSmsAuth);
  const phone = smsAuth?.phone ?? "";
  const temporarySmsToken = smsAuth?.temporarySmsToken ?? "";
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleVerifyCode = useCallback(
    async (value: string) => {
      setState({ status: "fetching" });
      try {
        await dispatch(
          verifySmsLoginOtpAction({
            phone,
            temporarySmsToken,
            confirmationCode: value,
          }),
        );
        setState({ status: "idle" });
      } catch (_error) {
        setState({ status: "error" });
      }
    },
    [dispatch, phone, temporarySmsToken],
  );

  const handleResendCode = useCallback(async () => {
    setState({ status: "fetching" });
    try {
      await dispatch(sendSmsLoginOtpAction({ phone }));
      setState({ status: "idle" });
    } catch (_error) {
      setState({ status: "error" });
    }
  }, [dispatch, phone]);

  const disabled = state.status === "fetching" || state.status === "success";

  return (
    <>
      <AuthInstructions
        values={{ phone: formatAndObscurePhoneNumber(phone) }}
      />
      <div className="form-container">
        <OtpInput
          length={4}
          onChange={handleVerifyCode}
          size="large"
          testID="otp-input"
          accessibilityLabel={t("screens.auth.smsVerify.ariaLabel")}
        />

        <div className="form-button-container">
          <AuthLinkButton
            disabled={disabled}
            onPress={handleResendCode}
            label={t("screens.auth.smsVerify.resendCode")}
          />
        </div>
      </div>

      {state.status === "error" && <AuthError />}
    </>
  );
};

export { SmsVerify };
